import React from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import { useApiData } from '../hooks/useApiData';
import { ScoreCard, SeriesBars, LoadingState, ErrorState } from '../components/primitives';
import RangeControl, { rangeLabel } from '../components/RangeControl';
import BillingSummary from '../components/BillingSummary';
import ClientUsersPanel from '../components/ClientUsersPanel';
import { codeOf, fmtInt, fmtMoney, timeAgo } from '../lib/format';
import { flagTone, statusTone } from '../lib/flags';
import { TimeRange } from '../types/superadmin';
import { rangeFromQuery, rangeToQuery } from '../lib/rangeParams';

export default function ClientDetail() {
  const { tenantId } = useParams<{ tenantId: string }>();
  // The range lives in the URL so the session-review pages this drill-down
  // links into inherit the same period, and so a chosen window survives a
  // reload or a back navigation.
  const [searchParams, setSearchParams] = useSearchParams();
  const range = rangeFromQuery(searchParams);
  const setRange = (next: TimeRange) =>
    setSearchParams(new URLSearchParams(rangeToQuery(next)), { replace: true });

  const { data, loading, error } = useApiData(
    () => api.getClient(tenantId as string, range),
    [tenantId, JSON.stringify(range)]
  );

  const detail = data?.data;

  if (loading) return <LoadingState />;
  if (error || !detail) return <ErrorState message={error || 'Client not found.'} />;

  const c = detail.snapshot;
  const { activity, series } = detail;
  // "Active" for the selected period = reps with any call/training activity.
  // detail.reps now includes the full roster (zero-activity reps padded as null
  // rows), so count only those with real activity here.
  const activeInPeriod = detail.reps.filter((r) => r.sessions > 0 || r.trainingSessions > 0).length;
  const inactiveInPeriod = Math.max(0, c.users.total - activeInPeriod);
  const sub = c.billing.subscription;
  const status = sub?.status || 'none';
  const heroTone = c.atRisk.isAtRisk ? 'neg' : statusTone(status);
  const statusLabel = c.atRisk.isAtRisk ? 'At risk' : status;

  return (
    <>
      {/* Breadcrumb + range */}
      <div className="filter-bar">
        <Link className="dd" to="/clients">
          ← All clients
        </Link>
        <span className="sep" />
        <span className="label">Client</span>
        <span className="dd-pill">{c.displayName}</span>
        <span className="grow" />
        <RangeControl value={range} onChange={setRange} />
      </div>

      {/* Client hero */}
      <div className="client-hero">
        <div>
          <div className="eyebrow">Clients · drilldown</div>
          <div className="client-id">
            <span className="logo">{codeOf(c.tenantId)}</span>
            <div>
              <h1 className="title" style={{ fontSize: 44, margin: '0 0 4px' }}>
                <span className="serif">{c.displayName}</span>
              </h1>
              <div className="meta-row">
                <span>{c.tenantId}</span>
                <span style={{ color: 'var(--ink-4)' }}>·</span>
                <span>{status}</span>
                <span style={{ color: 'var(--ink-4)' }}>·</span>
                <span>
                  {sub?.trialAccessEnabled
                    ? 'Unlimited seats (trial access)'
                    : `${sub?.usedUserSeats ?? 0}/${sub?.totalUserSeats ?? 0} seats`}
                </span>
                <span style={{ color: 'var(--ink-4)' }}>·</span>
                <span>{fmtMoney(c.billing.revenue.lifetime)} lifetime</span>
              </div>
            </div>
          </div>
          <p className="subtitle">
            Last session {timeAgo(c.usage.lastSessionAt)}. {c.users.active} of {c.users.total} users active.
            Activity below covers <b>{rangeLabel(range)}</b>.
          </p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
          <span className={'status-pill ' + heroTone}>
            <span className="dot" />
            {statusLabel}
          </span>
          {c.adminPortalUrl && (
            <a
              className="btn btn-primary"
              href={c.adminPortalUrl}
              target="_blank"
              rel="noreferrer"
              title="This client's own admin portal — sessions, post-call analysis, users"
            >
              Open admin portal →
            </a>
          )}
        </div>
      </div>

      {/*
        Scope scorecard — all range-scoped. Calls adherence + training are the
        SAME numbers the reps table below pools to (and the flags use), so the
        card, the reps aggregate, and "Why flagged" always match.
      */}
      <div className="scorecard">
        <ScoreCard label="Total users" value={fmtInt(c.users.total)} sub={`${activeInPeriod} active · ${inactiveInPeriod} inactive · ${rangeLabel(range)}`} />
        <ScoreCard label="Sessions" value={fmtInt(activity.sessions)} sub={rangeLabel(range)} />
        <ScoreCard label="Call minutes" value={fmtInt(activity.minutes)} sub={rangeLabel(range)} />
        <ScoreCard label="Call quality" value={detail.aggregate.callQualityScore ?? '—'} unit={detail.aggregate.callQualityScore != null ? '%' : undefined} sub={`Lushin rubric · ${rangeLabel(range)}`} />
        {/* Training is off for Lushin — re-enable this card when training turns on:
        <ScoreCard label="Training" value={detail.aggregate.trainingScore ?? '—'} unit={detail.aggregate.trainingScore != null ? '%' : undefined} sub={rangeLabel(range)} /> */}
        <ScoreCard label="Open tickets" value={fmtInt(c.support.openTickets)} sub={`${c.support.unreadFeedback} unread feedback`} />
      </div>

      {/* Knowledge base panel removed: it counted the live-intelligence
          components (objections / FAQs / landmines / competitors / playbooks)
          that this post-call-only fork never populates, so it reported "missing"
          on every client forever. Restore it here if LIVE_INTELLIGENCE_ENABLED
          is ever turned on. */}

      {/* Billing — trial vs subscription, list price vs actually paid */}
      <BillingSummary billing={c.billing} />

      {/* Users — one table: the live roster (managed for trial clients) joined
          to each user's range-scoped rubric scores. Replaces the separate
          "Users" and "Reps" tables, which listed the same people twice. */}
      <ClientUsersPanel
        tenantId={tenantId as string}
        clientName={c.displayName}
        reps={detail.reps}
        range={range}
      />

      {/* At-risk flags (calls/training follow the range; rest are current-state) */}
      {detail.flags.length > 0 && (
        <section className="section">
          <div className="section-head">
            <div>
              <h3>Why this client is flagged</h3>
              <p className="desc">
                Billing, KB and seat signals are current; calls/training reflect {rangeLabel(range)}.
              </p>
            </div>
          </div>
          <div className="flags">
            {detail.flags.map((f) => (
              <span key={f.key} className={'chip ' + flagTone(f.key)} title={f.label}>
                {f.detail || f.label}
              </span>
            ))}
          </div>
        </section>
      )}

      {/* By-day activity charts (range) */}
      <section className="section">
        <div className="split">
          <div>
            <div className="section-head">
              <div>
                <h3>Minutes by day</h3>
                <p className="desc">Call minutes, {rangeLabel(range)}.</p>
              </div>
            </div>
            <SeriesBars data={series} field="minutes" unit=" min" />
          </div>
          <div>
            <div className="section-head">
              <div>
                <h3>Sessions by day</h3>
                <p className="desc">Call sessions, {rangeLabel(range)}.</p>
              </div>
            </div>
            <SeriesBars data={series} field="sessions" />
          </div>
        </div>
      </section>

      {/* Open tickets (current) */}
      {c.support.openTicketList.length > 0 && (
        <section className="section">
          <div className="section-head">
            <div>
              <h3>Open support tickets</h3>
              <p className="desc">Oldest first.</p>
            </div>
          </div>
          <div className="tbl">
            <table>
              <thead>
                <tr>
                  <th>Ticket</th>
                  <th style={{ width: 120 }}>Severity</th>
                  <th style={{ width: 160 }}>Requester</th>
                  <th style={{ width: 140 }}>Opened</th>
                </tr>
              </thead>
              <tbody>
                {c.support.openTicketList.map((ticket) => (
                  <tr key={ticket.ticketId}>
                    <td>{ticket.title}</td>
                    <td>
                      <span className={'chip ' + (ticket.severity === 'high' ? 'neg' : ticket.severity === 'medium' ? 'warn' : '')}>
                        {ticket.severity}
                      </span>
                    </td>
                    <td className="mono" style={{ color: 'var(--ink-3)' }}>
                      {ticket.name || ticket.email || '—'}
                    </td>
                    <td>{timeAgo(ticket.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </>
  );
}
